import type { FC } from "react";

import classNames from "classnames";
import { useRouter } from "next/navigation";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button, type ButtonVariant } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { ctaInputs } from "./Cta.builder";

import styles from "./Cta.module.scss";

export type CtaBuilderProps = BuilderComponentProps<typeof ctaInputs>;

const buttonVariantMap: Record<NonNullable<CtaBuilderProps["color"]>, ButtonVariant> = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "text",
  quaternary: "text-muted",
  ghost: "ghost",
};

export const Cta: FC<CtaBuilderProps> = ({ label, url, variant, color, alignment = "center", trackingEvent = "" }) => {
  const router = useRouter();

  const classes = classNames(styles.cta, {
    [styles.alignStart as string]: alignment === "start",
    [styles.alignEnd as string]: alignment === "end",
  });

  const handleClick = async () => {
    if (trackingEvent) {
      await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
        cta_name: trackingEvent === "cta_click" ? label : trackingEvent,
        cta_location: "cta",
      });
    }

    router.push(url);
  };

  if (variant === "cta") {
    return (
      <Button onClick={handleClick} variant={buttonVariantMap[color]} className={classes}>
        {label}
      </Button>
    );
  }

  // This should never happen as we validate it in builder
  const redirectColor = color === "ghost" ? "primary" : color;
  return (
    <Button
      onClick={handleClick}
      variant={redirectColor === "tertiary" ? "text" : "text-muted"}
      size={redirectColor === "primary" ? "md" : "sm"}
      iconAfter={redirectColor === "primary" || redirectColor === "secondary" ? LongArrowForwardIcon : undefined}
      className={classes}
    >
      {label}
    </Button>
  );
};
